import { FC, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { RealtyImage, RealtyType } from 'shared/api/kyrgyzstan/search'
import { CurrencySom } from 'shared/constants'
import GhostImage from 'shared/images/ghost.png'
import { priceShow } from 'shared/lib/priceShow'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Carousel } from 'shared/ui/carousel'
import { ImageOptimized } from 'shared/ui/image-optimized'

import { TypeTag } from '../type-tag'

import s from './styles.module.scss'

export interface OfferCardProps {
  id: string
  title: string
  images: RealtyImage[]
  type: RealtyType
  price: number
  variant?: 'grid' | 'horizontal'
  onClick?: (id: string) => void
}

export const OfferCard: FC<OfferCardProps> = ({
  id,
  title,
  images,
  type,
  price,
  variant = 'grid',
  onClick,
}) => {
  const { t } = useTranslation()
  const firstImage = images[0]?.image_url

  const preparedImages = useMemo(() => {
    return images.map((image) => ({
      id: image.id,
      image: image.image_url,
      image_fullpath: image.image_url,
    }))
  }, [images])

  const handleCardClick = () => {
    onClick?.(id)
  }

  return (
    <div
      className={s.card}
      onClick={handleCardClick}
    >
      <div className={clsx(s.imageWrapper, s[`${variant}ImageWrapper`])}>
        {variant === 'horizontal' && firstImage ? (
          <ImageOptimized
            src={firstImage}
            alt={title}
            className={s.image}
            wrapperClassName={s.image}
            errorImage={GhostImage}
          />
        ) : preparedImages.length > 0 ? (
          <Carousel
            dotsVariant="custom"
            images={preparedImages}
            errorImage={GhostImage}
            showCounter
            size="square"
          />
        ) : (
          <div className={s.placeholder}>
            <AppIcon
              id={AllIcons.PIN}
              width={24}
              height={24}
            />
          </div>
        )}

        <TypeTag
          type={type}
          className={s.tag}
        />
      </div>

      <div className={s.content}>
        <h4 className={clsx(s.title, 'bold-14')}>{title}</h4>

        <p className={clsx(s.price, 'semi-bold-14')}>
          <span className={s.priceValue}>
            {priceShow(String(price))} {CurrencySom}
          </span>{' '}
          <span className={clsx(s.priceLabel, 'medium-14')}>
            {t('kyrgyzstan_block.detail.per_night')}
          </span>
        </p>
      </div>
    </div>
  )
}
